import { t } from '../../locale'
import { useState, useEffect, useMemo } from 'react'
import { Spinner } from 'react-bootstrap'
import { Card, Table, Dropdown, Badge, Button, ButtonGroup, Form, Col } from 'react-bootstrap'
import { formatDate, getAccessToken, getCompanyInfo, removeAccess, updateStatus } from '../../helpers/helpers'
import { useNavigate } from 'react-router-dom'

export const Index = () => {
    const navigate = useNavigate()
    const [requests, setRequests] = useState([] as Array<any>)
    const [loading, setLoading] = useState(false)
    const [filterStatus, setFilterStatus] = useState('')
    const [sort, setSort] = useState<{ key: 'firstName' | 'lastName' | 'email' | 'createdBy' | null, dir: 'asc' | 'desc' }>({
        key: null,
        dir: 'asc',
    })
    const [search, setSearch] = useState('')

    const companyId = getCompanyInfo()?.companyId

    useEffect(() => {
        const token = sessionStorage.getItem('accessToken')
        if (!token) {
            navigate('/ds_id_verification/login')
            return
        }
        if (!companyId) {
            navigate('/ds_id_verification/company-select')
            return
        }

        getRequests()
    }, [navigate, filterStatus])

    const toggleSort = (key: 'firstName' | 'lastName' | 'email' | 'createdBy') => {
        setSort(prev => {
            if (prev.key === key) {
                return { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
            }
            return { key, dir: 'asc' }
        })
    }
    const normalize = (v: any) => (v ?? '').toString().toLowerCase()

    const displayedRequests = useMemo(() => {

        const filtered = (requests ?? []).filter((r: any) => {
            const q = search.trim().toLowerCase()
            if (!q) return true;
            return (
                normalize(r.email).includes(q) ||
                normalize(r.firstName).includes(q) ||
                normalize(r.lastName).includes(q) ||
                normalize(r.createdBy).includes(q)
            )
        })

        if (!sort.key) return filtered

        const dir = sort.dir === 'asc' ? 1 : -1
        const key = sort.key

        return [...filtered].sort((a: any, b: any) => {
            const av = normalize(a[key])
            const bv = normalize(b[key])
            if (av < bv) return -1 * dir
            if (av > bv) return 1 * dir
            return 0
        })
    }, [requests, search, sort])

    async function getRequests() {

        const accessToken = getAccessToken()
        if (!!accessToken) {
            await apiRequest(
                `${process.env.REACT_APP_API_URL}/api/v1/request/all`,
                'POST',
                {
                    companyId: companyId,
                    filter: filterStatus
                },
                '',
                accessToken
            ).then(r => {
                setRequests(r.requests)
            })
        }
    }

    async function apiRequest(url: string, method: string, body?: any, apiKey?: string, accessToken?: any) {
        const headers: Record<string, string> = {
            'Content-Type': 'application/json'
        };

        if (apiKey) {
            headers['x-api-key'] = `${apiKey}`
        }

        if (accessToken) {
            headers['accessToken'] = `${accessToken}`
        }

        const response = await fetch(url, {
            method,
            headers,
            body: body ? JSON.stringify(body) : undefined
        });

        if (response.status === 403) {
            navigate('/ds_id_verification/login')
        }

        return response.json();
    }

    async function getFile(requestId: string, createdAt: string, fileType: 'pdf' | 'mp4') {
        if (!companyId) {
            console.error('companyId nije dostupan.')
            return
        }
        const fileName = fileType === 'pdf'
            ? `videoidentifikation-${createdAt.split('T')[0]}.pdf`
            : `video.mp4`;
        try {
            setLoading(true);
            const accessToken = getAccessToken()

            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/v1/report/download/admin/${requestId}/${fileName}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(accessToken ? { accessToken } : {})
                },
                body: JSON.stringify({
                    companyId: companyId,
                    requestId: requestId
                })
            });
            if (!response.ok) {
                throw new Error('Greška prilikom preuzimanja fajla.');
            }
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            // Ako je PDF, otvori u novom tabu, a ako je MP4, prikaži kao video
            if (fileType === 'pdf') {
                window.open(url, '_blank');
            } else {
                const videoWindow = window.open();
                if (videoWindow) {
                    videoWindow.document.write(`
                        <video controls autoplay style='width: 100%; height: 100%;'>
                            <source src='${url}' type='video/mp4'>
                            Video nije podržan u ovom browseru.
                        </video>
                    `);
                }
            }

        } catch (error) {

            console.error(error);

        } finally {
            setLoading(false);
        }

    }

    const handleStatusChange = async (id: string, newStatus: string) => {
        try {
            setLoading(true)
            if (companyId) {
                try {
                    await updateStatus(companyId, id, newStatus, 'admin', getAccessToken());
                    setRequests(prevRequests =>
                        prevRequests.map(request =>
                            request._id === id ? { ...request, status: newStatus } : request
                        )
                    );
                } catch (error) {
                    console.error('Error:', error);
                } finally {
                    setLoading(false)
                }
            }


        } catch (error) {
            console.error('Error:', error);
        }
    };

    const deleteRequest = async (id: string) => {
        try {
            setLoading(true);

            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/v1/request/delete`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    companyId: companyId,
                    requestId: id
                })
            });
            if (!response.ok) {
                throw new Error('Greška prilikom brisanja requesta');
            }

            setRequests(prevRequests =>
                prevRequests.filter(request => request._id !== id)
            );
        } catch (err) {
            console.error(err)
        } finally {
            setLoading(false)
        }

    }

    const handleLogout = () => {
        const accessToken = getAccessToken();
        if (accessToken) {
            const headers: Record<string, string> = {
                'Content-Type': 'application/json',
                'accessToken': accessToken
            };
            fetch(`${process.env.REACT_APP_API_URL}/api/v1/access`, {
                method: 'DELETE',
                headers: headers
            }).then(() => {
                removeAccess()
                navigate('/ds_id_verification/login');
            })
        }
    };

    function handleFilterStatus(e: any) {
        setFilterStatus(e.target.value)
    }

    function handleShowAll(e: any) {
        setFilterStatus('')
    }

    function fintsCheckColumn(request: any): 'yes' | 'no' {
        const date1 = new Date(request.createdAt)
        const cutoff = new Date('2025-08-20T12:00:00Z')

        return (date1 <= cutoff || request.fintsCheck === 1) ? 'yes' : 'no'
    }

    return (
        <>
            <div className='d-flex justify-content-between align-items-center p-3 bg-light shadow-sm border-bottom'>
                <h4 className='mb-0 fw-bold text-primary'>DS Identity - Data Security</h4>
                <button className='btn btn-outline-danger' onClick={handleLogout}>
                    {t('Logout')}
                </button>
            </div>
            {loading && (
                <div className='d-flex justify-content-center align-items-center position-fixed top-0 start-0 w-100 h-100 bg-white bg-opacity-75' style={{ zIndex: 9999 }}>
                    <Spinner animation='border' variant='primary' role='status' />
                    <span className='ms-2'>{t('Loading...')}</span>
                </div>
            )}
            <Card className='shadow-sm my-4'>
                <Card.Header className='bg-primary text-center py-3'>
                    <h5 className='mb-0 text-white'>{t('DS Identity')}</h5>
                </Card.Header>
            </Card>

            <Form className='row mt-3 align-items-center' onSubmit={handleFilterStatus}>
                <Col xs='auto' className='d-flex align-items-center'>
                    <Form.Label htmlFor='filterStatus' className='fw-semibold me-2'>{t('Status')}:</Form.Label>
                    <Form.Select id='filterStatus' value={filterStatus} onChange={handleFilterStatus} className='w-auto'>
                        <option value=''>{t('')}</option>
                        <option value='NEW'>{t('NEW')}</option>
                        <option value='INITIATED'>{t('INITIATED')}</option>
                        <option value='PENDING_PAYMENT'>{t('PENDING_PAYMENT')}</option>
                        <option value='SUBMITTED'>{t('SUBMITTED')}</option>
                        <option value='SUCCESS'>{t('SUCCESS')}</option>
                    </Form.Select>
                </Col>
                <Col className='d-flex'>
                    <Form.Control
                        type='search'
                        placeholder={t('searchPlaceholder')}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        style={{ maxWidth: 420 }}
                    />
                </Col>
                <Col className='text-end'>
                    <Button variant='secondary' onClick={handleShowAll} style={{ textTransform: 'none' }}>
                        {t('ShowAll')}
                    </Button>
                </Col>
            </Form>

            <Table responsive striped bordered className='mt-3 noWrap'>
                <thead className='text-center'>
                    <tr>
                        <th
                            role='button'
                            className='user-select-none'
                            onClick={() => toggleSort('firstName')}
                            title={t('Sort by first name')}
                        >
                            {t('firstName')} {sort.key === 'firstName' ? (sort.dir === 'asc' ? '▲' : '▼') : '▲'}
                        </th>

                        <th
                            role='button'
                            className='user-select-none'
                            onClick={() => toggleSort('lastName')}
                            title={t('Sort by last name')}
                        >
                            {t('lastName')} {sort.key === 'lastName' ? (sort.dir === 'asc' ? '▲' : '▼') : '▲'}
                        </th>
                        <th>{t('dateOfBirth')}</th>
                        <th
                            role='button'
                            className='user-select-none'
                            onClick={() => toggleSort('email')}
                            title={t('Sort by email')}
                        >
                            {t('email')} {sort.key === 'email' ? (sort.dir === 'asc' ? '▲' : '▼') : '▲'}
                        </th>
                        <th>{t('idNumber')}</th>
                        <th>{t('FinTS')}</th>
                        <th>{t('Status')}</th>
                        <th>{t('similarity')}</th>
                        <th
                            role='button'
                            className='user-select-none'
                            onClick={() => toggleSort('createdBy')}
                            title={t('Sort by createdBy')}
                        >
                            {t('createdBy')} {sort.key === 'createdBy' ? (sort.dir === 'asc' ? '▲' : '▼') : '▲'}
                        </th>
                        <th>{t('createdAt')}</th>
                        <th>{t('actions')}</th>
                    </tr>
                </thead>
                <tbody>
                    {displayedRequests && displayedRequests.length > 0 ? (
                        displayedRequests.map((request) => (
                            <>
                                <tr key={request._id}>
                                    <td className='text-center text-nowrap'>{request.firstName}</td>
                                    <td className='text-nowrap'>{request.lastName}</td>
                                    <td className='text-nowrap'>{request.dateOfBirth}</td>
                                    <td className='text-nowrap'>{request.email}</td>
                                    <td className='text-nowrap'>{request.idNumber}</td>
                                    <td className='text-nowrap'>{fintsCheckColumn(request)}</td>
                                    <td className='text-nowrap'>
                                        <Badge bg={request.status === 'SUCCESS' ? 'success' : 'warning'}>
                                            {request.status}
                                        </Badge>
                                    </td>
                                    <td className='text-nowrap'>{(request.similarity * 100).toFixed(2)}%</td>
                                    <td className='text-nowrap'>{request.createdBy}</td>
                                    <td className='text-nowrap'>{formatDate(new Date(request.createdAt))}</td>
                                    <td>

                                        <Dropdown>
                                            <Dropdown.Toggle variant='primary' size='sm'>
                                                {t('Documents')}
                                            </Dropdown.Toggle>

                                            <Dropdown.Menu flip={false}>
                                                {request.status === 'SUBMITTED' || request.status === 'SUCCESS' ? (
                                                    <>
                                                        <Dropdown.Item onClick={() => getFile(request._id, request.createdAt, 'pdf')}>
                                                            📄 {t('PDF')}
                                                        </Dropdown.Item><Dropdown.Item onClick={() => getFile(request._id, request.createdAt, 'mp4')}>
                                                            🎥 {t('Video')}
                                                        </Dropdown.Item>
                                                    </>
                                                ) : null}
                                                <>
                                                    <Dropdown.Item onClick={() => navigate(`admin/detail/${companyId}/${request._id}`)}>
                                                        {t('Details')}
                                                    </Dropdown.Item></>
                                            </Dropdown.Menu>
                                        </Dropdown>

                                    </td><td>
                                        {request.status === 'SUBMITTED' && (
                                            <ButtonGroup>
                                                <Button
                                                    variant='success'
                                                    size='sm'
                                                    onClick={() => handleStatusChange(request._id, 'SUCCESS')}
                                                >
                                                    ✔️ {t('AcceptDocument')}
                                                </Button>
                                                <Button
                                                    variant='danger'
                                                    size='sm'
                                                    onClick={() => handleStatusChange(request._id, 'FAILED')}
                                                >
                                                    ❌ {t('RejectDocument')}
                                                </Button>
                                            </ButtonGroup>
                                        )}
                                    </td><td>
                                        <Button
                                            variant='danger'
                                            size='sm'
                                            onClick={() => deleteRequest(request._id)}
                                        >
                                            x {t('Delete')}
                                        </Button>
                                    </td>
                                </tr >
                            </>

                        ))
                    ) : (
                        <tr>
                            <td colSpan={10} className='text-center py-4'>
                                <em>{t('noClientsFound')}</em>
                            </td>
                        </tr>
                    )}

                    <tr style={{ opacity: 0.5, height: '50px' }}>
                        <td colSpan={10}></td>
                    </tr>

                </tbody>
            </Table>
        </>
    )
}

export default Index;